import React, { useEffect, useRef, useState, useMemo } from "react";
import {
  Box,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  CircularProgress,
  IconButton,
  Stack,
  useTheme,
  Divider,
  Grid,
} from "@mui/material";
import CancelIcon from "@mui/icons-material/Cancel";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import DeleteIcon from "@mui/icons-material/Delete";
import LockIcon from "@mui/icons-material/Lock";
import capitalize from "lodash/capitalize";
import { useForm } from "react-hook-form";

import ConfirmationModal from "../Modals/ConfirmationModal";
import Avatar from "@mui/material/Avatar";
import ImageIcon from "@mui/icons-material/Image";
import UploadIcon from "@mui/icons-material/Upload";
import ImageCropper from "../../pages/Feed/ImageCropper";
import { PUT_WORKSPACE_IMAGE } from "../../api/group";
import { GET_IMAGE } from "../../helpers/images";
import SoftInput from "../SoftInput";
import SoftSelect from "../SoftSelect";
import SoftButton from "../SoftButton";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";

import SoftBox from "../SoftBox";
import SoftTypography from "../SoftTypography";
import { UseCompanyWorkspaces } from "../../hooks/company";

const WorskpacePanel = ({
  handleClose,
  handleSave,
  handleDelete,
  isLoading,
  types,
  group,
}) => {
  const { register, handleSubmit, reset, setValue, getValues, watch } = useForm({
    defaultValues: {
      image: "",
      name: "",
      typeId: group?.typeId || types?.data[0]?._id,
      isContainer: false,
      parentId: null,
    },
  });

  const imageInputRef = useRef();
  const [openDialog, setOpenDialog] = useState(false);
  const [imageSrc, setImageSrc] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const hasKpis = useMemo(() => !!group?.kpis?.length, [group]);
  const topWorkspaces = UseCompanyWorkspaces();
  const isContainer = watch("isContainer");
  const parentId = watch("parentId");

  const parentOptions = useMemo(() => {
    const selfId = group?._id ? String(group._id) : null;
    return [
      { value: "", label: "— None (top-level) —" },
      ...(topWorkspaces || [])
        .filter((w) => w && String(w._id) !== selfId)
        .map((w) => ({
          value: String(w._id),
          label: w.isContainer ? `${w.name} (group)` : w.name,
        })),
    ];
  }, [topWorkspaces, group?._id]);

  const theme = useTheme();
  const {
    primary: { main: primaryColor },
  } = theme.palette;

  const workspaceSelect =
    types?.data?.length &&
    types?.data?.map((type) => ({
      value: capitalize(type._id),
      label: capitalize(type.type),
    }));

  const selectedType = types?.data?.filter(
    (type) => type?._id === group?.typeId,
  )[0]?.type;

  const onSubmit = (data) => {
    const payload = {
      name: data.name,
      image: data.image,
      typeId: data.typeId,
      isContainer: !!data.isContainer && !data.parentId,
    };
    /* Only send parentId when editing — create stays top-level (use Add sub-workspace for new children). */
    if (group?._id) {
      payload.parentId = data.parentId || null;
    }
    handleSave(payload);
  };

  const handleUpload = () => {
    imageInputRef.current.value = null;
    imageInputRef.current.click();
  };

  const handleInputChange = async (e) => {
    if (e.target.files.length === 0) {
      return;
    }

    const imgSrc = e.target.files[0];
    if (imgSrc) {
      setImageSrc(URL.createObjectURL(imgSrc));
      setOpenDialog(true);
    }
  };

  const onSaveCroppedImage = async (croppedImageFile) => {
    if (!croppedImageFile) return;
    const formData = new FormData();
    setIsUploading(true);
    formData.append("image", croppedImageFile);
    PUT_WORKSPACE_IMAGE(formData)
      .then(({ data }) => {
        setImageSrc(data.key);
        setValue("image", data.key);
        setIsUploading(false);
      })
      .catch((err) => {
        console.log("Job image upload error", err.message);
        setIsUploading(false);
      });

    setOpenDialog(false);
    imageInputRef.current.value = null;
  };

  useEffect(() => {
    return () => {
      setIsUploading(false);
    };
  }, []);

  useEffect(() => {
    if (group) {
      reset({
        ...group,
        isContainer: !!group.isContainer,
        parentId: group.parentId ? String(group.parentId) : "",
      });
    }
  }, [group, reset]);

  return (
    <>
      <SoftBox
        display="flex"
        justifyContent="space-between"
        alignItems="center"
      >
        <SoftBox sx={{ display: "flex", gap: "0.5rem" }}>
          <SoftTypography variant="h4" sx={{ color: "#0F172A" }}>
            {group
              ? "Edit your workspace"
              : "Create a new workspace for your team"}
          </SoftTypography>
        </SoftBox>

        <SoftButton
          variant="outlined"
          color="dark"
          onClick={handleClose}
          sx={{
            "& svg": { fontSize: "1.25rem !important" },
            borderRadius: "50%",
            minWidth: "max-content",
            minHeight: "max-content",
            padding: "0.25rem",
          }}
        >
          <CloseRoundedIcon />
        </SoftButton>
      </SoftBox>
      <Divider
        sx={{
          backgroundColor: "rgba(15, 23, 42, 0.10)",
        }}
      />
      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={1} className="mt-2">
          <Grid item mini={12} marginBottom={2}>
            <Grid container spacing={2} className="mt-2">
              <Grid item mini={5} marginBottom={2}>
                <SoftBox
                  sx={{
                    height: "130px",
                    maxWidth: "100%",
                    background: "#04549C1A",
                    border: "1.9px dashed #04549C",
                    borderRadius: "8px",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "4px",
                  }}
                >
                  {!isUploading && !getValues("image") ? (
                    <SoftBox
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      <ImageIcon sx={{ fontSize: 100, color: "#6C757D" }} />
                      <SoftTypography
                        sx={{
                          color: "#6C757D",
                          fontSize: "16px",
                          fontWeight: 600,
                        }}
                      >
                        Upload Logo
                      </SoftTypography>
                    </SoftBox>
                  ) : (
                    <SoftBox sx={{ width: "100%", height: "100%" }}>
                      <Avatar
                        sx={{
                          width: "100%",
                          height: "100%",
                        }}
                        variant="rounded"
                        src={
                          !isUploading && getValues("image")
                            ? GET_IMAGE(getValues("image"))
                            : ""
                        }
                      >
                        {isUploading && <CircularProgress />}
                      </Avatar>
                    </SoftBox>
                  )}

                  <input
                    ref={imageInputRef}
                    onInput={handleInputChange}
                    accept="image/*"
                    type="file"
                    hidden
                  />
                  <ImageCropper
                    imgSrc={imageSrc}
                    open={openDialog}
                    setOpen={setOpenDialog}
                    onSaveCroppedImage={onSaveCroppedImage}
                  />
                </SoftBox>
              </Grid>
              <Grid
                item
                mini={6}
                marginBottom={2}
                container
                alignItems="center"
              >
                <SoftBox>
                  <SoftTypography
                    sx={{ color: "#344054", fontSize: "16px", fontWeight: 500 }}
                  >
                    Upload Your Workspace Logo
                  </SoftTypography>
                  <SoftTypography
                    sx={{
                      color: "#98A2B3",
                      fontSize: "13px",
                      fontWeight: 400,
                      marginBottom: "10px",
                    }}
                  >
                    JPEG, PNG or GIF. Max file size 10GB
                  </SoftTypography>
                  <SoftButton
                    color="outlined"
                    variant="contained"
                    startIcon={<UploadIcon sx={{ color: "#475467" }} />}
                    onClick={handleUpload}
                    sx={{
                      textTransform: "none",
                      borderRadius: "4px",
                      border: "1px solid rgba(15, 23, 42, 0.12)",
                      boxShadow: "0px 1px 2px 0px rgba(0, 0, 0, 0.05)",
                      background: "rgba(15, 23, 42, 0.03)",
                      color: "#475467",
                      "&:hover": {
                        background: "rgba(15, 23, 42, 0.06)",
                      },
                    }}
                  >
                    Upload
                  </SoftButton>
                </SoftBox>
              </Grid>
            </Grid>
          </Grid>
          <Grid item mini={12} marginBottom={2}>
            <SoftBox
              sx={{
                marginBottom: "0.25rem",
              }}
            >
              <SoftTypography variant="button" sx={{ color: "#344054" }}>
                Enter Your Workspace Name
              </SoftTypography>
            </SoftBox>
            <SoftInput
              {...register("name")}
              variant="outlined"
              size="large"
              placeholder="Your workspace name"
              sx={{
                border: "1px solid #E4E7EC",
                backgroundColor: "#F6F7F9 !important",
                color: "#1d2939 !important",
                "& .MuiInputBase-input::placeholder": {
                  color: "#98A2B3 !important",
                  opacity: "1",
                },
                "&.Mui-focused": {
                  borderColor: primaryColor,
                  boxShadow: `0rem 0rem 0rem 0.125rem ${primaryColor}`,
                },
              }}
            />
          </Grid>
          <Grid item mini={12} marginBottom={3}>
            <SoftBox
              sx={{
                marginBottom: "0.25rem",
              }}
            >
              <SoftTypography variant="button" sx={{ color: "#344054" }}>
                Select Workspace Type
              </SoftTypography>
            </SoftBox>
            <SoftSelect
              placeholder="Workspace type"
              size="large"
              {...register("typeId", { required: true })}
              disabled={group?._id}
              onChange={(option) => setValue("typeId", option.value)}
              options={workspaceSelect}
              defaultValue={{
                value: getValues("typeId"),
                label: selectedType || "Workspace type",
              }}
              styles={{
                color: "#1d2939 !important",
                control: {
                  background: "#F6F7F9 !important",
                },
                placeholder: {
                  color: "#98A2B3 !important",
                  opacity: "1",
                },
                input: {
                  color: "#1d2939 !important",
                  opacity: "1",
                },
                singleValue: {
                  color: "#1d2939 !important",
                  opacity: "1",
                },
              }}
            />
          </Grid>
          {group?._id && (
            <Grid item mini={12} marginBottom={2}>
              <SoftBox sx={{ marginBottom: "0.25rem" }}>
                <SoftTypography variant="button" sx={{ color: "#344054" }}>
                  Nest under (optional)
                </SoftTypography>
              </SoftBox>
              <SoftTypography
                sx={{
                  color: "#98A2B3",
                  fontSize: "12px",
                  fontWeight: 400,
                  marginBottom: "8px",
                  display: "block",
                }}
              >
                Groups this workspace in the sidebar without changing its data or metrics.
                Example: put Field Sales under Sales.
              </SoftTypography>
              <SoftSelect
                placeholder="Top-level (no parent)"
                size="large"
                value={
                  parentOptions.find((o) => o.value === (parentId || ""))
                  || parentOptions[0]
                }
                onChange={(option) => setValue("parentId", option?.value || "")}
                options={parentOptions}
                styles={{
                  color: "#1d2939 !important",
                  control: { background: "#F6F7F9 !important" },
                  singleValue: { color: "#1d2939 !important", opacity: "1" },
                }}
              />
            </Grid>
          )}
          {!parentId && (
            <Grid item mini={12} marginBottom={3}>
              <SoftBox
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: "10px",
                  padding: "12px 14px",
                  borderRadius: "8px",
                  border: "1px solid #E4E7EC",
                  background: "#F9FAFB",
                }}
              >
                <input
                  type="checkbox"
                  id="gmb-ws-container"
                  checked={!!isContainer}
                  onChange={(e) => setValue("isContainer", e.target.checked)}
                  style={{ marginTop: 3, width: 16, height: 16, cursor: "pointer" }}
                />
                <SoftBox>
                  <SoftTypography
                    component="label"
                    htmlFor="gmb-ws-container"
                    variant="button"
                    sx={{ color: "#344054", cursor: "pointer", display: "block" }}
                  >
                    Container only (group folder)
                  </SoftTypography>
                  <SoftTypography
                    sx={{
                      color: "#98A2B3",
                      fontSize: "12px",
                      fontWeight: 400,
                      display: "block",
                      marginTop: "2px",
                    }}
                  >
                    When selected, show nothing until a subworkspace is picked.
                    Use this for Sales when Field Sales and Inside Sales sit underneath.
                  </SoftTypography>
                </SoftBox>
              </SoftBox>
            </Grid>
          )}
        </Grid>
        <SoftBox
          display="flex"
          alignItems="center"
          justifyContent="space-between"
        >
          <ConfirmationModal
            title={hasKpis ? "Disable Workspace" : "Delete Workspace"}
            question={
              hasKpis
                ? "This workspace has KPIs; both workspace and its KPIs will be disabled. Continue?"
                : "This workspace has no KPIs and would be permanently deleted. Continue?"
            }
            onSuccess={handleDelete}
            noText="No, cancel"
            yesText={hasKpis ? "Yes, disable" : "Yes, delete"}
          >
            <SoftButton
              color="outlined"
              variant="contained"
              sx={{
                textTransform: "none",
                borderRadius: "4px",
                border: "1px solid rgba(15, 23, 42, 0.12)",
                boxShadow: "0px 1px 2px 0px rgba(0, 0, 0, 0.05)",
                background: "rgba(15, 23, 42, 0.03)",
                color: "#475467",
                "&:hover": {
                  background: "rgba(15, 23, 42, 0.06)",
                },
              }}
            >
              Delete
            </SoftButton>
          </ConfirmationModal>

          <SoftBox display="flex" alignItems="center" gap="0.5rem">
            <SoftButton
              variant="contained"
              color="outlined"
              onClick={handleClose}
              sx={{
                textTransform: "none",
                borderRadius: "4px",
                border: "1px solid rgba(15, 23, 42, 0.12)",
                boxShadow: "0px 1px 2px 0px rgba(0, 0, 0, 0.05)",
                background: "rgba(15, 23, 42, 0.03)",
                color: "#475467",
                "&:hover": {
                  background: "rgba(15, 23, 42, 0.06)",
                },
              }}
            >
              Cancel
            </SoftButton>
            <SoftButton
              variant="contained"
              color="primary"
              type="submit"
              disabled={isLoading}
              sx={{
                color: "primary.contrastText",
                textTransform: "none",
                borderRadius: "4px",
              }}
            >
              {group ? "Save Workspace" : "Create Workspace"}{" "}
              {isLoading && (
                <CircularProgress
                  color="inherit"
                  size="1rem"
                  className="ml-2"
                />
              )}
            </SoftButton>
          </SoftBox>
        </SoftBox>
      </form>
    </>
  );
};

export default WorskpacePanel;
